import { plural } from '@limuzin/kit';
import type { PassengerRideView } from '@limuzin/passenger';

/**
 * Where the search is, in the passenger's terms — `SearchStage` in the phone app's
 * `searching_screen.dart`, stage for stage and threshold for threshold, so the same
 * order reads the same on a phone and in a browser.
 *
 * Dispatch offers the ride to drivers one at a time first — `offering`, with no
 * clock, because nothing bad happens when that part is over. Then the ride goes into
 * «Свободные», where every driver on the line sees it, for the pool's minutes — the
 * other four, which do have a clock, because that deadline is real. They change so
 * that ten minutes are not ten minutes of one spinner, and the last minute says
 * plainly that the search is about to stop.
 */
export type SearchStage = 'offering' | 'open' | 'continuing' | 'longer' | 'lastMinute';

export interface SearchReading {
  stage: SearchStage;
  title: string;
  lead: string;
  /** Milliseconds left of the pool's minutes; null where nothing will give up. */
  left: number | null;
  /** `left` as a share of the pool's minutes: how much of the bar is filled. */
  share: number | null;
  /** «Ищем ещё до 8 минут» — the line under the bar. */
  barLabel: string | null;
}

const MINUTE = 60_000;

/**
 * The sentence under «Заказ доступен водителям». Not the phone's «Сообщим сразу»: a
 * closed tab is told nothing about a «сейчас» ride being taken — Web Push carries
 * only messages and a предзаказ setting off (ADR 0027) — so what this one promises is
 * what an open page does.
 */
const TOLD =
  'Теперь ваш заказ видят все свободные водители. Держите страницу открытой — покажем ' +
  'машину, как только кто-нибудь примет поездку.';

/** Whole minutes, rounded up: the last one before the end reads 1, not 0. */
const minutesUp = (ms: number): number => Math.ceil(ms / MINUTE);

/** «до 1 минуты», «до 8 минут» — the genitive «до» takes. */
const afterDo = (n: number): string => `${n} ${plural(n, 'минуты', 'минут', 'минут')}`;

/**
 * What the screen says for `ride` at `now`. Counted from the server's two instants,
 * so the stages follow whatever the pool's minutes are set to there.
 */
export function searchReading(ride: PassengerRideView, now: number): SearchReading {
  if (!ride.pooledAt) {
    return {
      stage: 'offering',
      title: 'Ищем водителя',
      lead: 'Предлагаем вашу поездку свободным водителям',
      left: null,
      share: null,
      barLabel: null,
    };
  }
  if (!ride.searchEndsAt) {
    return {
      stage: 'open',
      title: 'Заказ доступен водителям',
      lead: TOLD,
      left: null,
      share: null,
      barLabel: null,
    };
  }

  // A computer whose clock disagrees with the server's reads within the pool's own
  // minutes rather than outside them.
  const from = Date.parse(ride.pooledAt);
  const to = Date.parse(ride.searchEndsAt);
  const total = Math.max(0, to - from);
  const left = Math.min(total, Math.max(0, to - now));
  const gone = total - left;
  const share = total > 0 ? left / total : 0;
  const reading = (stage: SearchStage, title: string, lead: string): SearchReading => ({
    stage,
    title,
    lead,
    left,
    share,
    barLabel:
      stage === 'lastMinute' ? 'Осталось меньше минуты' : `Ищем ещё до ${afterDo(minutesUp(left))}`,
  });

  if (left <= MINUTE) {
    return reading(
      'lastMinute',
      'Пока водитель не найден',
      'Если никто не примет заказ, поиск завершится через 1 минуту',
    );
  }
  if (left <= 3 * MINUTE) {
    return reading(
      'longer',
      'Поиск занимает больше времени',
      'Можно подождать ещё немного или изменить заказ',
    );
  }
  if (gone >= 3 * MINUTE) {
    return reading('continuing', 'Поиск продолжается', 'Пока никто не принял заказ. Продолжаем искать');
  }
  return reading('open', 'Заказ доступен водителям', TOLD);
}

/**
 * How long the pool had been looking once it gave up — «За 10 минут никто…». Null
 * where the server did not say.
 */
export function searchMinutes(ride: PassengerRideView): number | null {
  if (!ride.pooledAt || !ride.searchEndsAt) return null;
  return Math.round((Date.parse(ride.searchEndsAt) - Date.parse(ride.pooledAt)) / MINUTE);
}
